import { useCallback, useEffect, useMemo, useState } from 'react';
import { BookOpen, Plus, Wrench, Car, ChevronRight, Video, AlertTriangle } from 'lucide-react';
import TabBar from '../components/layout/TabBar';
import ModulLayout from '../components/layout/ModulLayout';
import ListToolbar, { PohledPill } from '../components/layout/ListToolbar';
import usePotvrzeni from '../components/ui/usePotvrzeni';
import { REZERVA_LISTY } from '../components/MobilniLista';
import { useTheme } from '../contexts/ThemeContext';
import { SKLO_SVETLE } from '../lib/uiTokens';
import ClanekDetail from '../components/znalosti/ClanekDetail';
import ClanekEditor from '../components/znalosti/ClanekEditor';
import { ZarizeniDetail, ZarizeniEditor } from '../components/znalosti/Zarizeni';
import SmerniceSprava from '../components/znalosti/SmerniceSprava';
import { BARVA, BARVA_BG, TYPY, STAVY, api, delka, barva as barvaZnalosti } from '../components/znalosti/znalosti';

/**
 * Modul **Znalosti** (id `znalosti`) — návody k systému, jednobodovky (OPL)
 * a správa směrnic.
 *
 * Záložky: Jednobodovky · Návody · Směrnice · Zařízení. Norma rozvržení §1:
 * seznam přes celou šířku, po výběru seznam + detail; na mobilu jeden sloupec.
 * Směrnice (dřív samostatný modul `bezpecnost`, od 2026-10-11 tady) mají
 * vlastní komponentu `SmerniceSprava` se třetím sloupcem přehledu potvrzení.
 *
 * Číst smí každý (jen publikované články), psát a spravovat zařízení jen
 * administrátor — editor je modal, koncepty vidí jen on.
 *
 * Proklik zvenku přes `navParams`: `clanek` (panel ?, krok receptu, úkol),
 * `qr` (štítek na stroji), `zarizeni` (kus), `smernice` (se `tab=smernice`).
 */
const datumKratce = (d) => (d ? new Date(d).toLocaleDateString('cs', { day: 'numeric', month: 'numeric' }) : '');

export default function Znalosti({ user, initialTab = null, navParams = null }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const akcent = barvaZnalosti(isDark);

    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
    useEffect(() => {
        const h = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, []);

    const [tab, setTab] = useState(initialTab);
    const [data, setData] = useState(null);
    const [hledani, setHledani] = useState('');
    const [kategorie, setKategorie] = useState('vse');
    const [vybranyId, setVybranyId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [editor, setEditor] = useState(null);           // null | { puvodni?, vychoziTyp?, vychoziVazby? }
    const [zarizeniData, setZarizeniData] = useState(null);
    const [vybraneZarizeni, setVybraneZarizeni] = useState(null);   // 'zarizeni:3' | 'vozidlo:2'
    const [zarizeniEditor, setZarizeniEditor] = useState(null);     // null | 'nove' | řádek
    const [hlaska, setHlaska] = useState(null);
    const [potvrdit, potvrzeniDialog] = usePotvrzeni(isDark);

    // ── Data ──
    const nacti = useCallback(() => api('/api/znalosti').then(setData).catch(() => setData({ clanky: [], kategorie: [] })), []);
    useEffect(() => { nacti(); }, [nacti]);
    const nactiZarizeni = useCallback(() => api('/api/znalosti/zarizeni').then(setZarizeniData).catch(() => setZarizeniData({ zarizeni: [], vozidla: [], typy_stroju: [] })), []);
    useEffect(() => { if (tab === 'zarizeni' && !zarizeniData) nactiZarizeni(); }, [tab, zarizeniData, nactiZarizeni]);

    const nactiDetail = useCallback((id) => {
        if (!id) { setDetail(null); return Promise.resolve(null); }
        return api(`/api/znalosti/${id}`).then(d => { setDetail(d); return d; }).catch(() => { setDetail(null); return null; });
    }, []);
    useEffect(() => { if (vybranyId && detail?.id !== vybranyId) nactiDetail(vybranyId); }, [vybranyId, detail?.id, nactiDetail]);

    // ── Proklik zvenku ──
    const otevritClanek = useCallback(async (id) => {
        const d = await nactiDetail(id);
        if (!d) { setHlaska('Článek už není k dispozici.'); return; }
        setTab(d.typ === 'navod' ? 'navody' : 'jednobodovky');
        setVybranyId(d.id);
    }, [nactiDetail]);

    useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);
    useEffect(() => { if (navParams?.clanek) otevritClanek(Number(navParams.clanek)); }, [navParams?.clanek, otevritClanek]);
    useEffect(() => {
        if (!navParams?.qr) return;
        api(`/api/znalosti/qr/${encodeURIComponent(navParams.qr)}`).then(d => {
            setTab('zarizeni');
            setZarizeniData(null);
            setVybraneZarizeni(`zarizeni:${d.zarizeni.id}`);
            // Jediná jednobodovka? Rovnou ji otevřít — u stroje se nečte seznam
            if (d.clanky?.length === 1) otevritClanek(d.clanky[0].id);
        }).catch(() => setHlaska('Tenhle štítek nepatří žádnému zařízení.'));
    }, [navParams?.qr, otevritClanek]);
    useEffect(() => {
        if (!navParams?.zarizeni) return;
        setTab('zarizeni');
        setVybraneZarizeni(`zarizeni:${navParams.zarizeni}`);
    }, [navParams?.zarizeni]);

    // ── Vzhled ──
    const muted = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const cardStyle = isDark ? {
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 0.5px rgba(255,255,255,0.03)',
    } : SKLO_SVETLE;
    const inputStyle = {
        width: '100%', height: 32, borderRadius: 8, padding: '0 10px', outline: 'none',
        background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light',
    };
    const rezervaDole = isMobile ? REZERVA_LISTY : null;
    // Psát smí podle přístupů na záložku (upravuje) — backend vrací typy, které smí
    const smiPsat = data?.smi_psat || [];

    // ── Seznam článků ──
    const typTabu = tab === 'navody' ? 'navod' : 'jednobodovka';
    const clankyTabu = useMemo(() => (data?.clanky || []).filter(c => c.typ === typTabu), [data, typTabu]);
    const kategorieSPocty = useMemo(() => (data?.kategorie || [])
        .map(k => ({ ...k, pocet: clankyTabu.filter(c => c.kategorie_id === k.id).length }))
        .filter(k => k.pocet > 0), [data, clankyTabu]);
    const hledat = hledani.trim().toLowerCase();
    const seznam = clankyTabu.filter(c => (kategorie === 'vse' || c.kategorie_id === kategorie)
        && (!hledat || `${c.nazev} ${c.perex || ''} ${c.kod || ''} ${(c.vazby || []).map(v => v.popis).join(' ')}`.toLowerCase().includes(hledat)));
    useEffect(() => { setKategorie('vse'); }, [tab]);

    const pohledy = [{ id: 'vse', label: 'Vše', pocet: clankyTabu.length }, ...kategorieSPocty.map(k => ({ id: k.id, label: k.nazev, pocet: k.pocet }))];

    const radekClanku = (c, siroky) => {
        const aktivni = c.id === vybranyId;
        return (
            <button key={c.id} type="button" onClick={() => setVybranyId(c.id)} className="w-full text-left flex items-center"
                style={{ gap: 12, padding: isMobile ? '12px 14px' : '10px 14px', minHeight: 64, background: aktivni ? BARVA_BG : 'transparent', boxShadow: aktivni ? `inset 3px 0 0 ${BARVA}` : 'none', border: 'none', borderBottom: `1px solid ${cara}`, cursor: 'pointer' }}>
                <span className="grid place-items-center" style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0, overflow: 'hidden', background: BARVA_BG, color: akcent }}>
                    {c.nahled ? <img src={c.nahled} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <BookOpen size={19} />}
                </span>
                <span className="flex-1 min-w-0">
                    <span className="flex items-center" style={{ gap: 6 }}>
                        <span className="truncate font-semibold" style={{ color: 'var(--text-primary)', fontSize: isMobile ? 15 : 13.5 }}>{c.nazev}</span>
                        {c.kriticke && <AlertTriangle size={13} aria-label="Kritické" style={{ color: '#F59E0B', flexShrink: 0 }} />}
                        {c.stav !== 'publikovano' && (
                            <span className="shrink-0" style={{ fontSize: 10.5, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: c.stav === 'koncept' ? BARVA_BG : 'rgba(127,127,140,0.15)', color: c.stav === 'koncept' ? akcent : muted }}>{STAVY[c.stav]}</span>
                        )}
                    </span>
                    <span className="block truncate" style={{ margin: '2px 0 0', fontSize: isMobile ? 13 : 12, color: muted }}>
                        {c.kod ? `${c.kod} · ` : ''}
                        {c.typ === 'jednobodovka' && c.pocet_kroku ? `${c.pocet_kroku} kroků` : (c.perex || TYPY[c.typ].label)}
                        {c.video ? ` · video ${delka(c.video.delka_s) || ''}` : ''}
                        {!siroky && c.vazby?.length ? ` · ${c.vazby.slice(0, 2).map(v => v.popis).join(', ')}` : ''}
                    </span>
                </span>
                {siroky && <span className="shrink-0 truncate text-xs" style={{ width: 220, color: muted }}>{(c.vazby || []).map(v => v.popis).join(', ') || '—'}</span>}
                {siroky && <span className="shrink-0 text-xs tabular-nums" style={{ width: 90, color: muted, textAlign: 'right' }}>v{c.verze} · {datumKratce(c.upraveno_at)}</span>}
                {c.video && !siroky && <Video size={15} style={{ color: muted, flexShrink: 0 }} />}
            </button>
        );
    };

    const prazdno = (text) => <p className="text-center text-sm" style={{ color: muted, padding: '32px 16px' }}>{text}</p>;

    const seznamClanku = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar
                siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={BARVA}
                hledani={{ value: hledani, onChange: setHledani, placeholder: tab === 'navody' ? 'Hledat v návodech…' : 'Hledat v jednobodovkách…', placeholderKratky: 'Hledat' }}
                akce={smiPsat.includes(typTabu) ? [{ id: 'novy', label: tab === 'navody' ? 'Návod' : 'Jednobodovka', Icon: Plus, primarni: true, onClick: () => setEditor({ vychoziTyp: typTabu }) }] : []}
                pohledy={!siroky && !isMobile && pohledy.length > 1 ? pohledy : null}
                pohledAktivni={kategorie}
                onPohled={setKategorie}
                pohled={(siroky || isMobile) && pohledy.length > 1 ? pohledy.map(p => (
                    <PohledPill key={p.id} label={p.label} pocet={p.pocet} aktivni={kategorie === p.id} onClick={() => setKategorie(p.id)}
                        isDark={isDark} mutedColor={muted} barva={BARVA} kompaktni={isMobile} />
                )) : null}
            />
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {data === null && prazdno('Načítám…')}
                {data && seznam.length === 0 && prazdno(hledat ? 'Nic se neshoduje.' : tab === 'navody' ? 'Zatím žádné návody.' : 'Zatím žádné jednobodovky.')}
                {seznam.map(c => radekClanku(c, siroky))}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    const smazatClanek = async () => {
        if (!(await potvrdit('Smazat článek?', { text: 'Zmizí ze Znalostí i ze všech míst, kde byl navázaný.', potvrdit: 'Smazat', nebezpecne: true }))) return;
        await api(`/api/znalosti/${vybranyId}`, { method: 'DELETE' }).catch(() => {});
        setVybranyId(null); setDetail(null); nacti();
    };

    const detailClanku = () => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {detail && detail.id === vybranyId ? (
                    <ClanekDetail key={detail.id + ':' + detail.verze} clanek={detail} isDark={isDark} isMobile={isMobile}
                        onZpet={() => setVybranyId(null)} onUpravit={() => setEditor({ puvodni: detail })} onSmazat={smazatClanek}
                        onOtevritClanek={otevritClanek} rezervaDole={rezervaDole} />
                ) : prazdno('Načítám…')}
            </div>
        </div>
    );

    // ── Zařízení ──
    const objektyZarizeni = useMemo(() => [
        ...(zarizeniData?.zarizeni || []).map(z => ({ ...z, druh: 'zarizeni', klic: `zarizeni:${z.id}` })),
        ...(zarizeniData?.vozidla || []).map(v => ({ ...v, druh: 'vozidlo', klic: `vozidlo:${v.id}` })),
    ], [zarizeniData]);
    const vybranyObjekt = objektyZarizeni.find(o => o.klic === vybraneZarizeni) || null;
    const seznamZarizeniFiltr = objektyZarizeni.filter(o => !hledat || `${o.nazev || ''} ${o.kod || ''} ${o.stroj || ''} ${o.spz || ''} ${o.model || ''} ${o.umisteni || ''}`.toLowerCase().includes(hledat));

    const radekZarizeni = (o, siroky) => {
        const aktivni = o.klic === vybraneZarizeni;
        const jeAuto = o.druh === 'vozidlo';
        return (
            <button key={o.klic} type="button" onClick={() => setVybraneZarizeni(o.klic)} className="w-full text-left flex items-center"
                style={{ gap: 12, padding: isMobile ? '12px 14px' : '10px 14px', minHeight: 60, background: aktivni ? BARVA_BG : 'transparent', boxShadow: aktivni ? `inset 3px 0 0 ${BARVA}` : 'none', border: 'none', borderBottom: `1px solid ${cara}`, cursor: 'pointer', opacity: !jeAuto && !o.aktivni ? 0.55 : 1 }}>
                <span className="grid place-items-center" style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', color: muted }}>
                    {jeAuto ? <Car size={18} /> : <Wrench size={18} />}
                </span>
                <span className="flex-1 min-w-0">
                    <span className="block truncate font-semibold" style={{ color: 'var(--text-primary)', fontSize: isMobile ? 15 : 13.5 }}>{jeAuto ? o.spz : o.nazev}</span>
                    <span className="block truncate" style={{ fontSize: isMobile ? 13 : 12, color: muted, marginTop: 2 }}>
                        {jeAuto ? o.model : [o.kod, o.stroj, o.umisteni].filter(Boolean).join(' · ')}
                    </span>
                </span>
                <span className="shrink-0 text-xs tabular-nums" style={{ color: o.pocet_clanku ? akcent : muted, fontWeight: o.pocet_clanku ? 700 : 500 }}>
                    {o.pocet_clanku ? `${o.pocet_clanku} ${o.pocet_clanku === 1 ? 'článek' : o.pocet_clanku < 5 ? 'články' : 'článků'}` : siroky ? 'bez článků' : ''}
                </span>
                {!siroky && <ChevronRight size={16} style={{ color: muted, flexShrink: 0 }} />}
            </button>
        );
    };

    const seznamZarizeni = ({ siroky }) => {
        const kusy = seznamZarizeniFiltr.filter(o => o.druh === 'zarizeni');
        const auta = seznamZarizeniFiltr.filter(o => o.druh === 'vozidlo');
        const nadpis = (t) => <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: muted, margin: 0, padding: '14px 14px 6px' }}>{t}</p>;
        return (
            <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
                <ListToolbar
                    siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={BARVA}
                    hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat zařízení nebo auto…', placeholderKratky: 'Hledat' }}
                    akce={zarizeniData?.smi_spravovat ? [{ id: 'nove', label: 'Zařízení', Icon: Plus, primarni: true, onClick: () => setZarizeniEditor('nove') }] : []}
                />
                <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                    {zarizeniData === null && prazdno('Načítám…')}
                    {zarizeniData && kusy.length === 0 && auta.length === 0 && prazdno(hledat ? 'Nic se neshoduje.' : 'Zatím žádná zařízení.')}
                    {kusy.length > 0 && nadpis('Zařízení')}
                    {kusy.map(o => radekZarizeni(o, siroky))}
                    {auta.length > 0 && nadpis('Auta')}
                    {auta.map(o => radekZarizeni(o, siroky))}
                    {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
                </div>
            </div>
        );
    };

    const smazatZarizeni = async () => {
        if (!(await potvrdit('Smazat zařízení?', { text: 'QR štítek přestane fungovat a vazby článků na tento kus zmizí.', potvrdit: 'Smazat', nebezpecne: true }))) return;
        await api(`/api/znalosti/zarizeni/${vybranyObjekt.id}`, { method: 'DELETE' }).catch(() => {});
        setVybraneZarizeni(null); nactiZarizeni();
    };

    const detailZarizeni = () => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {vybranyObjekt ? (
                    <ZarizeniDetail objekt={vybranyObjekt} isDark={isDark} isMobile={isMobile} smiSpravovat={!!zarizeniData?.smi_spravovat}
                        smiPsatClanky={smiPsat.includes('jednobodovka')}
                        onZpet={() => setVybraneZarizeni(null)} onOtevritClanek={otevritClanek}
                        onUpravit={() => setZarizeniEditor(vybranyObjekt)} onSmazat={smazatZarizeni}
                        onNovyClanek={() => setEditor({ vychoziTyp: 'jednobodovka', vychoziVazby: [{ objekt_typ: vybranyObjekt.druh, objekt_id: vybranyObjekt.id, objekt_klic: null, popis: vybranyObjekt.nazev || vybranyObjekt.spz }] })}
                        rezervaDole={rezervaDole} />
                ) : prazdno(zarizeniData ? 'Zařízení nenalezeno.' : 'Načítám…')}
            </div>
        </div>
    );

    // ── Celek ──
    const jeZarizeni = tab === 'zarizeni';
    const jeSmernice = tab === 'smernice';
    return (
        // Mobil: připnutý modul jako Zprávy — od hlavičky ke spodní hraně, rezervu
        // pod lištou drží scroll uvnitř (app.jsx ho vede v `plnaVyskaMobil`)
        <div style={isMobile
            ? { position: 'fixed', top: 'var(--hlavicka-mobil)', left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 2 }
            : { height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {!isMobile && (
                <div className="flex items-center gap-3" style={{ flexShrink: 0, marginBottom: 12 }}>
                    <div className="grid place-items-center" style={{ width: 40, height: 40, borderRadius: 12, background: BARVA_BG, color: akcent }}><BookOpen size={20} /></div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Znalosti</h1>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                            {jeSmernice ? 'Směrnice, komu patří a kdo je potvrdil'
                                : `${data ? `${(data.clanky || []).filter(c => c.typ === 'jednobodovka').length} jednobodovek · ${(data.clanky || []).filter(c => c.typ === 'navod').length} návodů` : 'Načítám…'} · jak na to, krok za krokem`}
                        </p>
                    </div>
                </div>
            )}
            <div style={{ flexShrink: 0 }}>
                <TabBar modul="znalosti" user={user} aktivni={tab} onZmena={(t) => { setTab(t); setVybranyId(null); setHledani(''); }} barva={BARVA} isDark={isDark} isMobile={isMobile} />
            </div>
            {hlaska && (
                <p role="status" onClick={() => setHlaska(null)} style={{ margin: '10px 0 0', padding: '10px 14px', borderRadius: 10, fontSize: 13.5, background: 'rgba(245,158,11,0.12)', color: 'var(--text-primary)', border: '1px solid rgba(245,158,11,0.4)', cursor: 'pointer' }}>{hlaska}</p>
            )}
            <div className="flex flex-col" style={{ flex: 1, minHeight: 0, paddingTop: isMobile ? 0 : 16 }}>
                {tab && (jeSmernice ? (
                    <SmerniceSprava user={user} isDark={isDark} isMobile={isMobile} cardStyle={cardStyle} inputStyle={inputStyle}
                        muted={muted} cara={cara} rezervaDole={rezervaDole} vybranaZvenku={navParams?.smernice || null} />
                ) : jeZarizeni ? (
                    <ModulLayout key="zarizeni" isMobile={isMobile} vybrano={!!vybraneZarizeni} mobilRezim mobilPanel={false} barva={BARVA} isDark={isDark}
                        seznam={seznamZarizeni} detail={detailZarizeni} />
                ) : (
                    <ModulLayout key={tab} isMobile={isMobile} vybrano={!!vybranyId} mobilRezim mobilPanel={false} barva={BARVA} isDark={isDark}
                        seznam={seznamClanku} detail={detailClanku} />
                ))}
            </div>

            {editor && (
                <ClanekEditor isDark={isDark} puvodni={editor.puvodni || null} povoleneTypy={smiPsat} vychoziTyp={editor.vychoziTyp} vychoziVazby={editor.vychoziVazby || []}
                    kategorie={data?.kategorie || []} onClose={() => setEditor(null)}
                    onUlozeno={(c) => {
                        setEditor(null); nacti(); setDetail(c);
                        setTab(c.typ === 'navod' ? 'navody' : 'jednobodovky'); setVybranyId(c.id);
                        if (zarizeniData) nactiZarizeni();
                    }} />
            )}
            {zarizeniEditor && (
                <ZarizeniEditor isDark={isDark} puvodni={zarizeniEditor === 'nove' ? null : zarizeniEditor} typyStroju={zarizeniData?.typy_stroju || []}
                    onClose={() => setZarizeniEditor(null)}
                    onUlozeno={(z) => { setZarizeniEditor(null); nactiZarizeni(); setVybraneZarizeni(`zarizeni:${z.id}`); }} />
            )}
            {potvrzeniDialog}
        </div>
    );
}
